import { useEffect, useRef, useState } from 'react';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const MAPBOX_JS = 'https://unpkg.com/mapbox-gl@3/dist/mapbox-gl.js';
const MAPBOX_CSS = 'https://unpkg.com/mapbox-gl@3/dist/mapbox-gl.css';
const TULSA = [-95.9928, 36.154];

function loadMapbox() {
  if (window.__mapboxPromise) return window.__mapboxPromise;
  window.__mapboxPromise = new Promise((resolve, reject) => {
    try {
      if (!document.querySelector(`link[href="${MAPBOX_CSS}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = MAPBOX_CSS;
        document.head.appendChild(link);
      }
      if (window.mapboxgl) return resolve(window.mapboxgl);
      const script = document.createElement('script');
      script.src = MAPBOX_JS;
      script.onload = () =>
        window.mapboxgl ? resolve(window.mapboxgl) : reject(new Error('mapbox not available'));
      script.onerror = () => reject(new Error('mapbox blocked or unreachable'));
      document.head.appendChild(script);
    } catch (e) {
      reject(e);
    }
  });
  return window.__mapboxPromise;
}

export default function MapPreview({ pickupCoords, dropoffCoords, driverCoords, height = '300px', className = '' }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const [state, setState] = useState('loading');
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!MAPBOX_TOKEN) { setState('notoken'); return; }

    loadMapbox()
      .then((mapboxgl) => {
        if (cancelled || !containerRef.current || mapRef.current) return;
        mapboxgl.accessToken = MAPBOX_TOKEN;
        mapRef.current = new mapboxgl.Map({
          container: containerRef.current,
          style: 'mapbox://styles/mapbox/dark-v11',
          center: pickupCoords || TULSA,
          zoom: 12,
        });
        mapRef.current.on('error', () => {});
        mapRef.current.on('load', () => {
          if (cancelled) return;
          try {
            if (pickupCoords) new mapboxgl.Marker({ color: '#FFD400' }).setLngLat(pickupCoords).addTo(mapRef.current);
            if (dropoffCoords) new mapboxgl.Marker({ color: '#34d399' }).setLngLat(dropoffCoords).addTo(mapRef.current);
            if (pickupCoords && dropoffCoords) {
              const b = new mapboxgl.LngLatBounds();
              b.extend(pickupCoords); b.extend(dropoffCoords);
              mapRef.current.fitBounds(b, { padding: 60, maxZoom: 14 });
            }
          } catch (e) {}
          setMapReady(true);
        });
      })
      .catch(() => { if (!cancelled) setState('error'); });

    return () => {
      cancelled = true;
      if (mapRef.current) { try { mapRef.current.remove(); } catch (e) {} mapRef.current = null; }
      driverMarkerRef.current = null;
      setMapReady(false);
    };
  }, [pickupCoords, dropoffCoords]);

  // Live driver marker: create once, then just move it as new coords arrive.
  // Kept out of the map-creation effect so a GPS ping never rebuilds the map.
  // Also keyed on mapReady so coords that arrived before the map finished
  // loading still place the dot once the map is ready.
  useEffect(() => {
    const mapboxgl = window.mapboxgl;
    if (!driverCoords || !mapRef.current || !mapboxgl || !mapReady) return;
    if (!driverMarkerRef.current) {
      const el = document.createElement('div');
      el.style.cssText =
        'width:16px;height:16px;border-radius:50%;background:#3b82f6;' +
        'border:2px solid #fff;box-shadow:0 0 0 4px rgba(59,130,246,0.35);';
      driverMarkerRef.current = new mapboxgl.Marker({ element: el })
        .setLngLat(driverCoords)
        .addTo(mapRef.current);
    } else {
      driverMarkerRef.current.setLngLat(driverCoords);
    }
  }, [driverCoords, mapReady]);

  const box = { height, borderRadius: '1rem', overflow: 'hidden', background: '#111', border: '1px solid #333' };

  if (state === 'notoken' || state === 'error') {
    return (
      <div className={className} style={{ ...box, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: '#888', padding: '1rem' }}>
        <span>🗺️ {state === 'notoken' ? 'Map unavailable — token not set in this build.' : 'Map could not load right now. Everything else still works.'}</span>
      </div>
    );
  }
  return <div ref={containerRef} className={className} style={box} />;
}
